import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getAllJobs, getApplicantById, getApplications, postApplication } from '../../Utils/plcementServices';
import { AuthContext } from './authContext';
import { enqueueSnackbar } from 'notistack';

export type MatchLevel = 'excellent' | 'good' | 'average' | 'poor' | '';
export interface Job {
  jobId: string;
  logo: string;
  title: string;
  company: string;
  location: string;
  deadline: string;
  match: string;
  matchLevel: MatchLevel;
  tags: string[];
  salary: string;
  skills: string[];
  applicationSteps: { title: string; date: string }[];
  positionType: string;
  matchHits?: number;
  matchTotal?: number;
  matchRatio?: number;
  hiringSteps?: string[];
  aboutJob?: string;
  perks?: any;
  keyResponsibilities: string;
  otherRequirements: string;
}

type ApplicationStatus = 'not_applied' | 'applied' | 'interviewing' | 'offered' | 'rejected';

export interface ApplicationItem {
  logo: string;
  title: string;
  company: string;
  time: string;
  tags: any;
  salary: any;
  location: string;
  deadline: any;
  applicationStatus: ApplicationStatus;
  currentApplicationStepIndex?: number;
  applicationSteps: any;
  skills: any;
  Job_Application_Saved__c?: boolean;
  Jobs__c?: string;
}

type JobApplicationPayload = {
  Student__c: string;
  Jobs__c: string;
  Status__c: string;
  Job_Application_Saved__c: boolean;
  ResumePdf?: string;
  ResumeFileName?: string;
};

type PlacementUser = {
  applicantId?: string | null;
};

type MapJobsToUI = (jobs: Job[], preferredSkills?: string[]) => Job[];

type PlacementContextValue = {
  user?: PlacementUser;
  jobs: Job[];
  jobsLoading: boolean;
  getJobs: () => Promise<void>;
  applications: ApplicationItem[];
  applicationsLoading: boolean;
  getJobApplications: () => Promise<void>;
  applicant?: any;
  applicantLoading: boolean;
  getApplicant: () => Promise<void>;
  saveJobBookmark: (jobId: string, saved: Boolean) => Promise<boolean>;
  applyForJob: (args: { jobId: string; resume: string; resumeFileName?: string }) => Promise<{ ok: boolean; error?: string }>;
  error?: string | null;
  clearError: () => void;
  savedJobIds: string[];
  appliedJobIds: string[];
  setAppliedJobIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSavedJobIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const PlacementContext = createContext<PlacementContextValue | undefined>(undefined);

type ProviderProps = {
  children: React.ReactNode;
  mapJobsToUI?: MapJobsToUI;
};

export const PlacementProvider: React.FC<ProviderProps> = ({ children }) => {
  const {
    state: { user },
  } = useContext(AuthContext);

  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [applications, setApplications] = useState<ApplicationItem[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = () => setError(null);
  const [appliedJobIds, setAppliedJobIds] = useState<string[]>([]);
  const [savedJobIds, setSavedJobIds] = useState<string[]>([]);
  const [applicant, setApplicant] = useState<any>(null);
  const [applicantLoading, setApplicantLoading] = useState(false);

  const getJobs = useCallback(async () => {
    setJobsLoading(true);
    setError(null);
    try {
      const res = await getAllJobs();
      if (res?.data) setJobs(res.data as Job[]);
    } catch (err: any) {
      console.error(err);
      setError(err?.message ?? 'Failed to fetch jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  const getJobApplications = useCallback(async () => {
    if (!user?.applicantId) return;
    setApplicationsLoading(true);
    setError(null);
    try {
      const res = await getApplications(user.applicantId);
      if (res?.data) setApplications(res.data as ApplicationItem[]);
    } catch (err: any) {
      console.error(err);
      setError(err?.message ?? 'Failed to fetch applications');
    } finally {
      setApplicationsLoading(false);
    }
  }, [user?.applicantId]);

  const saveJobBookmark = useCallback(
    async (jobId: string, saved: Boolean) => {
      if (!user?.applicantId) return false;
      setError(null);

      try {
        const payload: JobApplicationPayload[] = [
          {
            Student__c: user.applicantId!,
            Jobs__c: jobId,
            Status__c: 'In Process',
            Job_Application_Saved__c: !saved,
          },
        ];

        const res = await postApplication(payload);
        if (!res) throw new Error('Bookmark failed');

        enqueueSnackbar(saved ? 'Job removed from saved' : 'Job saved successfully', {
          variant: saved ? 'info' : 'success',
          autoHideDuration: 1500,
        });

        // Show UI immediately
        setSavedJobIds((prev) => (saved ? prev.filter((id) => id !== jobId) : [...prev, jobId]));
        await getJobApplications();

        return true;
      } catch (err: any) {
        console.error(err);
        setError(err?.message ?? 'Failed to bookmark job');
        return false;
      }
    },
    [user?.applicantId, getJobApplications]
  );

  const applyForJob = useCallback(
    async ({ jobId, resume, resumeFileName = 'Resume' }: { jobId: string; resume: string; resumeFileName?: string }): Promise<{ ok: boolean; error?: string }> => {
      if (!user?.applicantId) return { ok: false, error: 'No applicantId' };
      setError(null);
      const payload: JobApplicationPayload[] = [
        {
          Student__c: user.applicantId,
          Jobs__c: jobId,
          Status__c: 'Applied',
          Job_Application_Saved__c: true,
          ResumePdf: resume,
          ResumeFileName: resumeFileName,
        },
      ];
      try {
        const res = await postApplication(payload);
        if (!res) throw new Error('Failed to apply');
        await getJobApplications();
        enqueueSnackbar({ message: 'Job applied successfully!', variant: 'success', autoHideDuration: 1000 });
        return { ok: true };
      } catch (err: any) {
        console.error(err);
        const message = err?.message ?? 'Failed to apply';
        setError(message);
        enqueueSnackbar({ message: 'Failed to apply', variant: 'error', autoHideDuration: 1000 });
        return { ok: false, error: message };
      }
    },
    [user?.applicantId, getJobApplications]
  );

  useEffect(() => {
    if (user) getJobs();
  }, [getJobs]);

  useEffect(() => {
    if (user) getJobApplications();
  }, [user?.applicantId, getJobApplications]);

  const getApplicant = useCallback(async () => {
    if (!user?.applicantId) return;
    setApplicantLoading(true);
    setError(null);
    try {
      const res = await getApplicantById(user.applicantId); // always from logged-in user
      if (res) setApplicant(res);
    } catch (err: any) {
      console.error(err);
      setError(err?.message ?? 'Failed to fetch applicant details');
    } finally {
      setApplicantLoading(false);
    }
  }, [user?.applicantId]);

  const value = useMemo<PlacementContextValue>(
    () => ({
      user: user ?? undefined,
      jobs,
      jobsLoading,
      getJobs,
      applications,
      applicationsLoading,
      getJobApplications,
      saveJobBookmark,
      applyForJob,
      error,
      clearError,
      appliedJobIds,
      savedJobIds,
      setAppliedJobIds,
      setSavedJobIds,
      applicant,
      applicantLoading,
      getApplicant,
    }),
    [
      user,
      jobs,
      jobsLoading,
      getJobs,
      applications,
      applicationsLoading,
      getJobApplications,
      saveJobBookmark,
      applyForJob,
      error,
      appliedJobIds,
      setAppliedJobIds,
      savedJobIds,
      setSavedJobIds,
      applicant,
      applicantLoading,
      getApplicant,
    ]
  );

  return <PlacementContext.Provider value={value}>{children}</PlacementContext.Provider>;
};

export const usePlacement = (): PlacementContextValue => {
  const ctx = useContext(PlacementContext);
  if (!ctx) {
    throw new Error('usePlacement must be used within a PlacementProvider');
  }
  return ctx;
};
